import React, { useContext } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { StyledProductCard, StyledProductImage, StyledProductBody, StyledProductTitle, StyledProductPrice, StyledImagePlaceholder } from "./style.css";
import routes from "../../../Helpers/Routes.json";
import { useTranslation } from "react-i18next";
import { LoadingComponent } from "../../Loading";
import { useProductsData } from "../../../Shared/Hooks/useProductsData";
import { formatCurrency } from "../../../Helpers/currency";
import { Context } from "../../../Shared/Context/GlobalContext";

export const ProductsGrid = ({ category, selectedCategory }) => {
  const { products, productsLoading } = useProductsData(
    category ? category.id : selectedCategory ? selectedCategory.id : null
  );

  if (productsLoading) {
    return <LoadingComponent />;
  }

  if (!products.length) {
    return null;
  }

  const filtered = products.filter(
    (p) => p.catalog_visibility === "visible" && p.status === "publish"
  );

  if (!filtered.length) {
    return <LoadingComponent />;
  }

  return (
    <Row>
      {filtered.map((product) => (
        <ProductCard key={`product-${product.id}`} {...product} />
      ))}
    </Row>
  );
};

export const ProductCard = ({
  id,
  slug,
  name,
  price,
  images,
  type,
  stock_status,
  product_variations,
  link = true,
  opacity = true,
  as,
  cardClickCallback = () => true,
  customFooter = null,
  textPrice = null,
  customCSS,
  cardCloseBtn,
  stock_quantity,
}) => {
  const { t } = useTranslation();
  const params = useParams();
  const { cartProducts, addCartProducts, updateCart } = useContext(Context);
  const img = images && images.length >= 1 ? images[0] : {};
    let price_label = formatCurrency(price);

  if (product_variations && product_variations.length) {
    let prices = product_variations.map((v) =>
      v.sales_price ? parseFloat(v.sales_price) : parseFloat(v.regular_price)
    );
    price_label = t("Desde ") + formatCurrency(Math.min(...prices));
  }

  if (type === "grouped") {
    price_label = "";
  }

  let WrapperLink = Link;
  if (as) {
    WrapperLink = as;
  }

  const isOutOfStock = stock_status !== "instock";
  const imgSrc = img.src;
  const hasImage = !!imgSrc;

  let cartQty = 0;
  let cartIndex = -1;
  let variationEntries = [];
  let bundleEntries = [];

  if (!isOutOfStock) {
    for (let i = 0; i < cartProducts.length; i++) {
      const cp = cartProducts[i].product;
      if (cp.id === id) {
        if (type === "simple" && cp.type === "simple") {
          cartQty += cp.quantity;
          if (cartIndex === -1) cartIndex = i;
        } else if (type === "variable" && cp.type === "variable") {
          cartQty += cp.quantity;
          if (cp.variation) {
            variationEntries.push({
              index: i,
              name: cp.variation.name.replace(cp.name + " - ", "").replace(cp.name + " ", ""),
              qty: cp.quantity,
              variationId: cp.variation.id,
              price: cp.price,
              maxStock: cp.maxStock || 999,
            });
          }
        } else if ((type === "grouped" || type === "bundle") && (cp.type === "grouped" || cp.type === "bundle")) {
          cartQty += cp.quantity;
          if (cartIndex === -1) cartIndex = i;
          if (cp.bundle && cp.bundle.length) {
            bundleEntries.push({
              index: i,
              items: cp.bundle.map(({ product: bp }) =>
                bp.type === "variable" && bp.variation
                  ? t(bp.name) + " · " + t(bp.variation.name.replace(bp.name + " - ", "").replace(bp.name + " ", ""))
                  : t(bp.name)
              ),
              qty: cp.quantity,
              price: cp.price,
              bundle: cp.bundle,
              maxStock: cp.maxStock && cp.maxStock !== 1 ? cp.maxStock : 999,
            });
          }
        }
      }
    }
  }

  const maxStock = stock_quantity || 999;
  const canAdd = cartQty < maxStock;
  const showCartBar = cartQty > 0 && !isOutOfStock && stock_status === "instock";

  return (
    <Col
      key={id}
      xs={6}
      sm={6}
      md={4}
      lg={3}
      className="mb-4"
      onClick={cardClickCallback}
    >
      <WrapperLink
        to={routes.ProductPage.get
          .replace(":category", params.category)
          .replace(":productId", slug)}
        className="text-decoration-none"
      >
        <StyledProductCard
          className={`border-0 ${!link && opacity ? "opacity-75" : ""} ${
            customCSS || ""
          }`}
        >
          {cardCloseBtn && (
            <span className="position-absolute top-0 end-0 m-2 z-3">
              {cardCloseBtn}
            </span>
          )}
          <StyledProductImage className="position-relative w-100">
            {hasImage ? (
              <Card.Img alt={img.alt || name} src={imgSrc} className="w-100" />
            ) : (
              <StyledImagePlaceholder className="w-100 h-100 d-flex align-items-center justify-content-center">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--tws-text-muted)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21,15 16,10 5,21"/>
                </svg>
              </StyledImagePlaceholder>
            )}
            {isOutOfStock && (
              <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-50">
                <span className="badge bg-dark rounded-pill px-3 py-2">
                  {t("Agotado")}
                </span>
              </div>
            )}
          </StyledProductImage>
          <StyledProductBody className="px-2 py-3">
            <StyledProductTitle>{t(name)}</StyledProductTitle>
            <StyledProductPrice className="mt-1">
              {textPrice
                ? textPrice
                : isOutOfStock
                ? t("Agotado")
                : price_label}
            </StyledProductPrice>
            {stock_quantity > 0 && stock_quantity < 3 && (
              <small className="text-muted d-block mt-1">
                {t("Quedan ") + stock_quantity}
              </small>
            )}
            {customFooter}
          </StyledProductBody>
        </StyledProductCard>
      </WrapperLink>

      {showCartBar && type === "simple" && (
        <div
          className="d-flex align-items-center justify-content-between px-2 py-1 rounded-pill"
          style={{
            backgroundColor: "var(--tws-muted)",
            marginTop: "-4px",
            position: "relative",
            zIndex: 2,
          }}
        >
          <button
            className="btn btn-sm p-0 border-0 d-flex align-items-center justify-content-center"
            style={{
              width: "26px",
              height: "26px",
              fontSize: "1rem",
              color: "var(--tws-brown)",
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              updateCart(cartIndex, "reduce");
            }}
          >
            −
          </button>
          <span className="small fw-medium" style={{ userSelect: "none" }}>
            {t("x{{qty}} en carrito", { qty: cartQty })}
          </span>
          <button
            className="btn btn-sm p-0 border-0 d-flex align-items-center justify-content-center"
            style={{
              width: "26px",
              height: "26px",
              fontSize: "1rem",
              color: "var(--tws-brown)",
              opacity: canAdd ? 1 : 0.3,
            }}
            disabled={!canAdd}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              addCartProducts({
                product: {
                  id,
                  name,
                  type,
                  quantity: 1,
                  price,
                  maxStock,
                },
              });
            }}
          >
            +
          </button>
        </div>
      )}

      {showCartBar && type !== "simple" && (
        <div
          className="rounded-3 px-2 py-1"
          style={{
            backgroundColor: "var(--tws-muted)",
            marginTop: "-4px",
            position: "relative",
            zIndex: 2,
          }}
        >
          {type === "variable" && variationEntries.length > 0 ? (
            variationEntries.map((entry, idx) => (
              <div
                key={`var-${entry.variationId}`}
                className={`d-flex align-items-center justify-content-between ${
                  idx > 0 ? "border-top pt-1 mt-1" : ""
                }`}
                style={{
                  borderColor: "var(--tws-border)",
                }}
              >
                <span className="small text-truncate me-1" style={{ maxWidth: "60%", userSelect: "none" }}>
                  {t(entry.name)}
                </span>
                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                  <button
                    className="btn btn-sm p-0 border-0 d-flex align-items-center justify-content-center"
                    style={{
                      width: "22px",
                      height: "22px",
                      fontSize: "0.85rem",
                      color: "var(--tws-brown)",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      updateCart(entry.index, "reduce");
                    }}
                  >
                    −
                  </button>
                  <span className="small fw-medium" style={{ minWidth: "16px", textAlign: "center", userSelect: "none" }}>
                    {entry.qty}
                  </span>
                  <button
                    className="btn btn-sm p-0 border-0 d-flex align-items-center justify-content-center"
                    style={{
                      width: "22px",
                      height: "22px",
                      fontSize: "0.85rem",
                      color: "var(--tws-brown)",
                      opacity: entry.qty < entry.maxStock ? 1 : 0.3,
                    }}
                    disabled={entry.qty >= entry.maxStock}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      addCartProducts({
                        product: {
                          id,
                          name,
                          type,
                          quantity: 1,
                          price: entry.price,
                          variation: { id: entry.variationId },
                          maxStock: entry.maxStock,
                        },
                      });
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
            ))
          ) : (type === "grouped" || type === "bundle") && bundleEntries.length > 0 ? (
            bundleEntries.map((entry, idx) => (
              <div
                key={`bundle-${entry.index}`}
                className={`${idx > 0 ? "border-top pt-1 mt-1" : ""}`}
                style={{ borderColor: "var(--tws-border)" }}
              >
                {entry.items.map((item, iIdx) => (
                  <div key={`item-${iIdx}`} className="small text-truncate" style={{ userSelect: "none" }}>
                    {item}
                  </div>
                ))}
                <div className="d-flex align-items-center justify-content-end gap-1 mt-1">
                  <button
                    className="btn btn-sm p-0 border-0 d-flex align-items-center justify-content-center"
                    style={{
                      width: "22px",
                      height: "22px",
                      fontSize: "0.85rem",
                      color: "var(--tws-brown)",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      updateCart(entry.index, "reduce");
                    }}
                  >
                    −
                  </button>
                  <span className="small fw-medium" style={{ minWidth: "16px", textAlign: "center", userSelect: "none" }}>
                    {entry.qty}
                  </span>
                  <button
                    className="btn btn-sm p-0 border-0 d-flex align-items-center justify-content-center"
                    style={{
                      width: "22px",
                      height: "22px",
                      fontSize: "0.85rem",
                      color: "var(--tws-brown)",
                      opacity: entry.qty < entry.maxStock ? 1 : 0.3,
                    }}
                    disabled={entry.qty >= entry.maxStock}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      addCartProducts({
                        product: {
                          id,
                          name,
                          type,
                          quantity: 1,
                          price: entry.price,
                          bundle: entry.bundle,
                          maxStock: entry.maxStock,
                        },
                      });
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-1">
              <span className="small fw-medium" style={{ userSelect: "none" }}>
                {t("x{{qty}} en carrito", { qty: cartQty })}
              </span>
            </div>
          )}
        </div>
      )}
    </Col>
  );
};
